import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, ShieldAlert, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { log } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Landing page for the admin-invite welcome email magic link. Verifies
 * the token on mount, shows the account's email, and accepts a chosen
 * password — on success the backend sets an auth cookie + returns a
 * JWT, so we hand the user straight into `/dashboard` already signed in.
 */
const WelcomePage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const token = params.get('token') || '';

  const [phase, setPhase] = useState('verifying'); // verifying | form | done | error
  const [verified, setVerified] = useState(null); // { email, first_name }
  const [err, setErr] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      if (!token) {
        setPhase('error');
        setErr('This welcome link is missing its token.');
        return;
      }
      try {
        const r = await axios.post(`${API_URL}/api/auth/welcome/verify`, { token });
        if (cancelled) return;
        setVerified(r.data);
        setPhase('form');
      } catch (e) {
        if (cancelled) return;
        setPhase('error');
        setErr(e?.response?.data?.detail || 'This welcome link is invalid or has expired.');
      }
    };
    verify();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (password.length < 8) {
      setErr('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setErr('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/auth/welcome/complete`, { token, password });
      // Refresh AuthContext so the app knows we're signed in, then
      // redirect to the customer dashboard.
      await refreshAuth?.();
      setPhase('done');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (e2) {
      log.error('Welcome complete failed:', e2);
      setErr(e2?.response?.data?.detail || 'Could not activate your account. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F1D14] via-[#1a2a1f] to-[#0F1D14] p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl ring-1 ring-[#4A7C59]/20 overflow-hidden" data-testid="welcome-page">
        {/* Brand hero — VIP Club artwork retired (iter114s.33). The card
            now leads straight into the activation form so newcomers see
            their actual welcome message first. */}

        <div className="p-8">
          {phase === 'verifying' && (
            <div className="text-center py-6" data-testid="welcome-verifying">
              <Loader2 className="w-8 h-8 animate-spin text-[#4A7C59] mx-auto mb-3" />
              <p className="text-sm text-gray-600">Checking your invitation…</p>
            </div>
          )}

          {phase === 'error' && (
            <div className="text-center py-2" data-testid="welcome-error">
              <ShieldAlert className="w-10 h-10 text-amber-600 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-[#2F5D3A] mb-1">This invitation can't be used</h2>
              <p className="text-sm text-gray-600 mb-5">{err}</p>
              <button
                onClick={() => navigate('/login')}
                className="inline-block bg-[#4A7C59] hover:bg-[#3D6A4A] text-white px-6 py-2.5 rounded-md font-semibold"
                data-testid="welcome-error-login-btn"
              >
                Go to sign-in
              </button>
            </div>
          )}

          {phase === 'form' && verified && (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="welcome-form">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#4A7C59] font-bold">
                  Hello {verified.first_name || 'there'},
                </p>
                <h1 className="text-2xl font-extrabold text-[#0F2B18] leading-tight mt-1">
                  Congratulations — your EG3 account has been created.
                </h1>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                  Your strategic partner in the promotional products industry
                  starts here. Set a password to finish activating your account.
                </p>
              </div>

              <div className="bg-[#F4F7F2] rounded-md p-3 text-sm border border-[#dbe5d6]">
                <span className="text-gray-600">Signed up as</span>
                <div className="font-semibold text-[#2F5D3A]" data-testid="welcome-email">
                  {verified.email}
                </div>
              </div>

              <div>
                <label htmlFor="wp" className="block text-sm font-medium text-gray-700 mb-1">
                  Choose a password
                </label>
                <div className="relative">
                  <input
                    id="wp"
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10 focus:outline-none focus:ring-2 focus:ring-[#4A7C59]"
                    data-testid="welcome-password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">At least 8 characters.</p>
              </div>

              <div>
                <label htmlFor="wc" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm password
                </label>
                <input
                  id="wc"
                  type={showPwd ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  minLength={8}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4A7C59]"
                  data-testid="welcome-confirm"
                  autoComplete="new-password"
                />
              </div>

              {err && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="welcome-form-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-[#6FBF73] via-[#4A7C59] to-[#2F5D3A] hover:from-[#4A7C59] hover:to-[#2F5D3A] text-white font-semibold py-3 rounded-md disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="welcome-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />Activating…</>
                ) : 'Activate My Account'}
              </button>
            </form>
          )}

          {phase === 'done' && (
            <div className="text-center py-6" data-testid="welcome-done">
              <CheckCircle2 className="w-10 h-10 text-[#4A7C59] mx-auto mb-3" />
              <h2 className="text-xl font-bold text-[#2F5D3A] mb-1">You're in!</h2>
              <p className="text-sm text-gray-600">Taking you to your dashboard…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;
